/**
 * Platform asset URLs for the campaign creator UI itself.
 *
 * 🤖 **AI agents read this first:** see `/AGENTS.md` at the repo root for the
 * full procedure on adding/modifying assets and the rules that catch the
 * common mistakes (wrong bucket, bundled PNGs, hardcoded URLs, etc).
 *
 * NOT to be confused with `assetBuckets.ts` (campaign assets — logos, markers,
 * AR objects, backgrounds that end up *inside* a built campaign, served via
 * the user-app at runtime). See the README section "Two asset systems" for
 * the full distinction.
 *
 * Platform assets live in the splash bucket, are public, and are served via
 * a dedicated CloudFront distribution. They are static, ops-managed, and
 * never customer-touched.
 *
 * Override the base URL per-environment with `VITE_SPLASH_ASSET_BASE_URL`.
 *
 * The default below intentionally points at the raw CloudFront domain
 * (d31o52qfgtm2zj.cloudfront.net) instead of the pretty alias
 * builder-assets.dev.viviewer.com declared in environments/dev.yml. The
 * pretty alias was deployed but the ACM certificate it inherited from the
 * frontend distribution only covers builder.dev.viviewer.com and its
 * subdomains, not the sibling builder-assets.* hostname. Until a proper
 * cert is issued and attached to the splash distribution, the raw
 * CloudFront domain serves the same content with a valid cert.
 *
 * Follow-up: see Jira ticket — request ACM cert for
 * builder-assets.dev.viviewer.com and flip this default back.
 */

const DEFAULT_SPLASH_BASE_URL = 'https://d31o52qfgtm2zj.cloudfront.net';

const rawBase =
  (import.meta.env.VITE_SPLASH_ASSET_BASE_URL as string | undefined)?.trim() ||
  DEFAULT_SPLASH_BASE_URL;

const splashBase = rawBase.replace(/\/+$/, '');
const platformRoot = `${splashBase}/campaign-creator`;

const join = (folder: string, fileName: string): string => {
  const cleanName = fileName.replace(/^\/+/, '');
  return `${platformRoot}/${folder}/${cleanName}`;
};

/**
 * Resolve a campaign-creator UI icon (e.g. `star.png`).
 * Stored at: s3://{splashBucket}/campaign-creator/icons/<name>
 */
export const platformIcon = (fileName: string): string => join('icons', fileName);

/**
 * Resolve a splash background decoration (e.g. `blue-top.png`).
 * Stored at: s3://{splashBucket}/campaign-creator/splash-backgrounds/<name>
 */
export const splashBackground = (fileName: string): string =>
  join('splash-backgrounds', fileName);

/**
 * Resolve a splash illustration (e.g. `case-studies.png`).
 * Stored at: s3://{splashBucket}/campaign-creator/illustrations/<name>
 */
export const platformIllustration = (fileName: string): string =>
  join('illustrations', fileName);

/**
 * Resolve a campaign-creator background option thumbnail used in picker UIs.
 * Stored at: s3://{splashBucket}/campaign-creator/background-option-thumbnails/<name>
 */
export const backgroundOptionThumbnail = (fileName: string): string =>
  join('background-option-thumbnails', fileName);

/**
 * Resolve a campaign-creator logo option thumbnail used in picker UIs.
 * Stored at: s3://{splashBucket}/campaign-creator/logo-option-thumbnails/<name>
 */
export const logoOptionThumbnail = (fileName: string): string =>
  join('logo-option-thumbnails', fileName);

export const platformAssetRoot = platformRoot;
